import clsx from 'clsx'
import type { ReactElement, ReactNode } from 'react'
import { formatDateShort } from '~/utils/date'
import { useApplicantShortlistFeedback } from '../queries/orderShortlistFeedbacks'
import { useApplicantShortlistUserComment } from '../queries/orderShortlistUserComments'
import {
  commentsWrapper,
  conversation,
  conversationAuthor,
  conversationBubble,
  conversationBubbleReply,
  conversationMessage,
  conversationMessageReply,
  conversationMeta
} from './OrderApplicantComments.css'
import { ShortlistFeedbackList } from './ShortlistFeedbackList'
import { feedbackListTitle } from './ShortlistRankBadge.css'

type ConversationMessage = {
  author: string
  text: string
  date: string
  fromContact: boolean
}

type OrderApplicantCommentsProps = {
  orderId?: number
  applicantId: number
  internalNote: ReactNode | null
}

export const OrderApplicantComments = ({
  orderId,
  applicantId,
  internalNote
}: OrderApplicantCommentsProps): ReactElement | null => {
  const userComment = useApplicantShortlistUserComment(orderId, applicantId)
  const feedback = useApplicantShortlistFeedback(orderId, applicantId)

  const messages: ConversationMessage[] = []
  if (userComment) {
    messages.push({
      author: userComment.senderName ?? 'Utilisateur',
      text: userComment.comment,
      date: userComment.sentAt,
      fromContact: false
    })
  }
  if (feedback?.comment) {
    messages.push({
      author: feedback.contactName ?? 'Contact',
      text: feedback.comment,
      date: feedback.submittedAt,
      fromContact: true
    })
  }
  messages.sort(
    (first, second) =>
      new Date(first.date).getTime() - new Date(second.date).getTime()
  )

  if (internalNote === null && messages.length === 0 && !feedback) {
    return null
  }

  return (
    <div className={commentsWrapper}>
      {internalNote}
      {messages.length > 0 && (
        <>
          <div className={feedbackListTitle}>Échange avec le client</div>
          <div className={conversation}>
            {messages.map((message) => (
              <div
                key={message.date}
                className={clsx(
                  conversationMessage,
                  message.fromContact && conversationMessageReply
                )}
              >
                <span className={conversationMeta}>
                  <span className={conversationAuthor}>{message.author}</span>
                  {' · '}
                  {formatDateShort(new Date(message.date))}
                </span>
                <div
                  className={clsx(
                    conversationBubble,
                    message.fromContact && conversationBubbleReply
                  )}
                >
                  {message.text}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {feedback && <ShortlistFeedbackList feedbacks={feedback.feedbacks} />}
    </div>
  )
}
